import React, { type FC, Suspense, useCallback, useMemo, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
import { defineMessages, useIntl } from 'react-intl-next';
import { graphql, useLazyLoadQuery } from 'react-relay';


import { IconButton, type IconButtonProps } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import PlaylistAddIcon from '@atlaskit/icon-lab/core/playlist-add';
import PlaylistAddedIcon from '@atlaskit/icon-lab/core/playlist-added';
import PlaylistRemoveIcon from '@atlaskit/icon-lab/core/playlist-remove';
import { Box } from '@atlaskit/primitives/compiled';
import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import type { TrackType } from '../../../api/cc-smarts/models/TrackType';
import type { ActiveContent } from '../../../types/activeContent';
import type { FlagsContainer } from '../../context/FlagsProviderContext';
import { getConfluenceContentAri } from '../../effects/getConfluenceContentAri';
import { useAudioPlayerStore } from '../../hooks/useAudioPlayerStore';
import { useContextMenuActions } from '../../hooks/useContextMenuActions';

import type { ModifyPlaylistButtonsQuery } from './__generated__/ModifyPlaylistButtonsQuery.graphql';
import type { PlaylistItemData } from './PlaylistItem';

export interface baseProps {
	testId?: string;
	title?: string;
	circle?: boolean;
	subtle?: boolean;
	/** Use FlagsProviderContext when possible. This prop overrides the context value.
	 * Sample valid use-cases: FeedAudioPlaylistButton, AudioPlaylistButton
	 */
	flagsOverride?: FlagsContainer;
	iconColor?: 'var(--ds-icon-subtlest)'; // Only enable subtlest color for icon
	playlist?: PlaylistItemData[];
}

interface playlistMutationArgs {
	cloudId: string;
	contentId: string;
	trackType?: `BRIEFING` | `NARRATION`;
	top?: boolean;
	contentType: string;
}

export interface ModifyPlaylistButtonProps extends baseProps {
	_defaultEngagement_do_not_use?: boolean;
	round?: boolean;
	cloudId: string;
}

const messages = defineMessages({
	addToPlaylistText: {
		id: 'ai-audio.playlist.button.addToPlaylist-label',
		defaultMessage: 'Add to playlist',
		description: 'Label for the chevron up icon to collapse the playlist',
	},
	removeFromPlaylistText: {
		id: 'ai-audio.playlist.button.removeFromPlaylist-label',
		defaultMessage: 'Remove from playlist',
		description: 'Label for the chevron down icon to expand the playlist',
	},
	inPlaylistText: {
		id: 'ai-audio.playlist.button.inPlaylist-label',
		defaultMessage: 'Added to playlist',
		description: 'Label for the chevron down icon to expand the playlist',
	},
} as const);

const styles = cssMap({
	container: {
		//typecheck refuses to allow space.400 tokens into height or width
		height: '32px',
		width: '32px',
	},
});

export const AddToPlaylistButton: FC<baseProps & playlistMutationArgs> = ({
	testId, //for testing
	cloudId, //needed to generate ari
	contentId, //needed to mutate
	contentType, //needed to mutate and for ari
	trackType = 'BRIEFING', //optional default: NARRATION or BRIEFING
	circle, //optional to apply circle shape
	subtle, //optional to apply subtle treatment
	flagsOverride, //optional override for context
	iconColor, //optional override for icon color
}) => {
	const { formatMessage } = useIntl();
	const { addTrack, isAdding } = useContextMenuActions(cloudId, flagsOverride);
	const { activeContent, isPlaying } = useAudioPlayerStore();

	const addToPlaylist = useCallback(async () => {
		const addingPlayingTrack = isPlaying && activeContent.contentId === contentId;
		await addTrack(
			getConfluenceContentAri(cloudId, contentId, contentType)?.toString() || '',
			activeContent.supportingContent.map((content: { ari: string }) => content.ari) || [],
			trackType as TrackType,
			{
				top: addingPlayingTrack || false,
			},
		);
	}, [
		cloudId,
		contentId,
		contentType,
		trackType,
		addTrack,
		activeContent.supportingContent,
		activeContent.contentId,
		isPlaying,
	]);

	const tooltipOptions: IconButtonProps['tooltip'] = {
		content: formatMessage(messages.addToPlaylistText),
		position: 'bottom',
		hideTooltipOnClick: true,
	};

	return (
		<IconButton
			icon={(iconProps) => (
				<PlaylistAddIcon {...iconProps} color={iconColor ? iconColor : undefined} />
			)}
			testId={testId && `${testId}-add`}
			label={formatMessage(messages.addToPlaylistText)}
			tooltip={tooltipOptions}
			onClick={addToPlaylist}
			isTooltipDisabled={false}
			isDisabled={isAdding || !contentId}
			shape={circle ? 'circle' : undefined}
			appearance={subtle ? 'subtle' : undefined}
			data-vc="add-to-playlist-button"
		/>
	);
};

export const RemoveFromPlaylistButton: FC<baseProps & playlistMutationArgs> = ({
	testId, //for testing
	title, //for success flag text
	cloudId, //needed to generate ari
	contentId, //needed to mutate
	contentType, //needed to mutate and for ari
	trackType, //optional default: NARRATION or BRIEFING
	circle, //optional to apply circle shape
	subtle, //optional to apply subtle treatment
	flagsOverride, //optional override for context
	iconColor, //optional override for icon color
	playlist, //modify playlist button will provide a list of items in the playlist
}) => {
	const { formatMessage } = useIntl();
	const { removeTrack, isRemoving } = useContextMenuActions(cloudId, flagsOverride);
	const { activeContent } = useAudioPlayerStore();

	const removeFromPlaylist = useCallback(async () => {
		await removeTrack(
			getConfluenceContentAri(cloudId, contentId, contentType)?.toString() || '',
			activeContent.supportingContent.map((content: { ari: string }) => content.ari) || [],
			(trackType || 'BRIEFING') as TrackType,
			title,
			{
				//type conversion because uggghhhhh relay fields
				playlist: playlist as unknown as ActiveContent[],
			},
		);
	}, [
		cloudId,
		contentId,
		contentType,
		trackType,
		title,
		removeTrack,
		activeContent.supportingContent,
		playlist,
	]);

	const tooltipOptions: IconButtonProps['tooltip'] = {
		content: formatMessage(messages.removeFromPlaylistText),
		position: 'bottom',
		hideTooltipOnClick: true,
	};

	return (
		<IconButton
			icon={(iconProps) => (
				<PlaylistRemoveIcon {...iconProps} color={iconColor ? iconColor : undefined} />
			)}
			testId={testId && `${testId}-remove`}
			label={formatMessage(messages.removeFromPlaylistText)}
			tooltip={tooltipOptions}
			onClick={removeFromPlaylist}
			isTooltipDisabled={false}
			isDisabled={isRemoving || !contentId}
			shape={circle ? 'circle' : undefined}
			appearance={subtle ? 'subtle' : undefined}
			data-vc="remove-from-playlist-button"
		/>
	);
};

//note: when using ModifyPlaylistButton abstraction the tooltip will not be seen because it changes to remove button on hover
export const InPlaylistButton: FC<baseProps> = ({ testId, circle, subtle, iconColor }) => {
	const { formatMessage } = useIntl();

	const tooltipOptions: IconButtonProps['tooltip'] = {
		content: formatMessage(messages.inPlaylistText),
		position: 'bottom',
		hideTooltipOnClick: true,
	};
	return (
		<IconButton
			icon={(iconProps) => (
				<PlaylistAddedIcon {...iconProps} color={iconColor ? iconColor : undefined} />
			)}
			testId={testId && `${testId}-added`}
			label={formatMessage(messages.inPlaylistText)}
			tooltip={tooltipOptions}
			isTooltipDisabled={false}
			shape={circle ? 'circle' : undefined}
			appearance={subtle ? 'subtle' : undefined}
			data-vc="in-playlist-button"
		/>
	);
};

export const LazyModifyPlaylistButton: FC<ModifyPlaylistButtonProps & playlistMutationArgs> = ({
	testId, // test ID
	title, //text for remove flag
	round, //applies rounded treatment
	subtle, //applies subtle treatment
	_defaultEngagement_do_not_use = false,
	cloudId, //needed to generate ari
	contentId, //needed to mutate
	contentType, //needed to mutate and for ari
	trackType, //optional default: NARRATION or BRIEFING
	flagsOverride, //optional override for context
	iconColor, //optional override for icon color
}: ModifyPlaylistButtonProps & playlistMutationArgs) => {
	const playlist = useLazyLoadQuery<ModifyPlaylistButtonsQuery>(
		graphql`
			query ModifyPlaylistButtonsQuery($cloudId: ID!) {
				confluence_getPlaylist(cloudId: $cloudId) {
					tracks {
						nodes {
							id
							type
							mainContent {
								id
								title
								type
								status
								subType
								space {
									key
								}
								history {
									createdBy {
										displayName
										profilePicture {
											path
										}
									}
								}
								links {
									base
									webui
								}
								properties(keys: ["emoji-title-published"]) {
									nodes {
										value
									}
								}
							}
						}
					}
				}
			}
		`,
		{ cloudId },
		{ fetchPolicy: 'store-or-network' },
	);

	const playlistItems: PlaylistItemData[] = useMemo(() => {
		const tracks = (playlist as any).confluence_getPlaylist?.tracks?.nodes || [];

		return tracks
			.filter((track: any) => {
				const mainContent = track.mainContent;
				const hasMainContent = !!mainContent;
				const hasValidStatus = mainContent?.status === 'current' || mainContent?.status === 'draft';
				return hasMainContent && hasValidStatus;
			})
			.map((track: any) => {
				const mainContent = track.mainContent;
				const supportingContent = track.supportingContent;
				const creator = mainContent.history?.createdBy;
				const avatarUrl = creator?.profilePicture?.path || undefined;

				return {
					contentId: mainContent.id,
					spaceKey: mainContent.space?.key || '',
					contentType: mainContent.type || '',
					title: mainContent.title || undefined,
					author: creator?.displayName || undefined,
					avatarUrl,
					mainContentLink: mainContent.links?.base + mainContent.links?.webui || '',
					supportingContent:
						supportingContent?.length > 0
							? supportingContent.map((content: any) => ({
									ari: content.ari,
									link: content.links?.base + content.links?.webui || '',
								}))
							: [],
					subtype: mainContent.subType || '',
					emojiId: mainContent.properties?.nodes?.[0]?.value || undefined,
				};
			});
	}, [playlist]);

	const isInPlaylist = useMemo(() => {
		if (!playlist?.confluence_getPlaylist?.tracks?.nodes) {
			return false;
		}

		const nodes = playlist.confluence_getPlaylist.tracks.nodes;
		// @ts-ignore nodes isn't strongly typed
		const matchPlaylist = (node) => {
			return trackType
				? node?.mainContent?.id === contentId && node.type === trackType
				: node?.mainContent?.id === contentId;
		};
		return nodes.find(matchPlaylist);
	}, [playlist, contentId, trackType]);

	const [isEngaged, setIsEngaged] = useState(_defaultEngagement_do_not_use);

	return (
		<ErrorBoundary fallbackRender={() => <div />}>
			<Box
				// Set the state to true when the mouse enters or the element receives focus
				onMouseEnter={() => setIsEngaged(true)}
				onFocus={() => setIsEngaged(true)}
				// Set the state back to false when the mouse leaves or the element loses focus
				onMouseLeave={() => setIsEngaged(false)}
				onBlur={() => setIsEngaged(false)}
				xcss={styles.container}
				data-vc="modify-playlist-button"
			>
				{isInPlaylist ? (
					!isEngaged ? (
						<InPlaylistButton
							testId={testId}
							circle={round}
							subtle={subtle}
							iconColor={iconColor}
						/>
					) : (
						<RemoveFromPlaylistButton
							iconColor={iconColor}
							testId={testId}
							cloudId={cloudId}
							contentId={contentId}
							contentType={contentType}
							trackType={isInPlaylist?.type as TrackType}
							title={title}
							circle={round}
							subtle={subtle}
							flagsOverride={flagsOverride}
							playlist={playlistItems}
						/>
					)
				) : (
					<AddToPlaylistButton
						iconColor={iconColor}
						testId={testId}
						cloudId={cloudId}
						contentId={contentId}
						contentType={contentType}
						trackType={trackType}
						circle={round}
						subtle={subtle}
						flagsOverride={flagsOverride}
					/>
				)}
			</Box>
		</ErrorBoundary>
	);
};

export const ModifyPlaylistButton: FC<ModifyPlaylistButtonProps & playlistMutationArgs> = (
	props: ModifyPlaylistButtonProps & playlistMutationArgs,
) => {
	return (
		<AtlassianRelayEnvironmentProvider>
			<Suspense fallback={null}>
				<LazyModifyPlaylistButton {...props} />
			</Suspense>
		</AtlassianRelayEnvironmentProvider>
	);
};
